'use strict'

/**
 * Advanced-section data-presence predicates + save-time advisories.
 *
 * Answers: "is the 'advanced' tab being used in any meaningful way?"
 *
 * Advanced-contract classification derived from contract data presence.
 * No persisted `advanced` boolean — single source of truth, no sync risk
 * between predicate and field.
 */

import {safeArray, safeString} from './_shared'
import {referenceFeeScheduleHasContent} from './fees'

// Non-USD currency is the advanced signal. Missing/null falls back to USD (legacy contracts had no field).
export const hasCurrencyOverride = (contract) => {
  if (!contract) return false
  const currency = contract.currency
  if (!currency) return false
  return currency !== 'USD'
}

// Any non-whitespace text counts. Editor normalizes empty/whitespace to null on save; predicate defends against un-normalized intermediate shape.
export const hasMaterialAcceptanceCriteria = (contract) => {
  if (!contract) return false
  return safeString(contract.materialAcceptanceCriteria).trim().length > 0
}

// Delegates to 'referenceFeeScheduleHasContent' so the predicate and the editor's save-time normalization share the same emptiness definition.
export const hasReferenceFeeScheduleContent = (contract) => {
  if (!contract) return false
  return referenceFeeScheduleHasContent(contract.referenceFeeSchedule)
}

// Any populated 'tierQualifyingMetal' counts. Validator gates the 'tierDimension !== purityPct' combination separately; this predicate fires on field presence.
export const hasTierQualifyingMetal = (contract) => {
  if (!contract) return false
  return Boolean(contract.tierQualifyingMetal)
}

// List-existence is the gate; empty array normalizes to null on save, both count as no modifiers.
export const hasSlaModifiers = (contract) => {
  if (!contract) return false
  return safeArray(contract.slaModifiers).length > 0
}

// Mixed-basis predicate: drives the editor's 'Basis' column visibility and the contract-list mixed-basis icon.
export const hasMixedFeeBasis = (contract) => {
  if (!contract) return false
  return safeArray(contract.metal_rates).some((row) => (
    row && row.feeBasis === 'netWeightReceivedToz'
  ))
}

// Any otherFees row with non-empty purityTiers counts. Empty array normalizes to null on save.
export const hasOtherFeesPurityTiers = (contract) => {
  if (!contract) return false
  return safeArray(contract.otherFees).some((row) => (
    row && Array.isArray(row.purityTiers) && row.purityTiers.length > 0
  ))
}

// Contract is "advanced" iff ANY section gate fires. Drives the 'UpstreamContractsList' 'mdi-tune' icon.
export const isContractAdvanced = (contract) => (
  hasCurrencyOverride(contract)
  || hasMaterialAcceptanceCriteria(contract)
  || hasReferenceFeeScheduleContent(contract)
  || hasTierQualifyingMetal(contract)
  || hasSlaModifiers(contract)
  || hasMixedFeeBasis(contract)
  || hasOtherFeesPurityTiers(contract)
)

// Save-time advisories: non-blocking amber warnings for cross-field combinations that probably indicate a mistake. Hard-error overlaps live in 'validateContractDraft' and pre-empt this at the editor.
export const warningsForDraft = (draft) => {
  if (!draft) return []
  const warnings = []

  // Reference fee schedule populated but currency still USD — the
  // schedule's per-line USD/EUR/etc. labels probably don't match.
  if (hasReferenceFeeScheduleContent(draft) && !hasCurrencyOverride(draft)) {
    warnings.push('fee schedule entries are in USD — confirm currency selection')
  }

  // Treatment ladder (otherFees row with purityTiers) without intake
  // gates usually means the operator copied the rate ladder from a PDF
  // without also capturing the acceptance criteria.
  if (hasOtherFeesPurityTiers(draft) && !hasMaterialAcceptanceCriteria(draft)) {
    warnings.push('treatment ladder usually pairs with acceptance criteria — review?')
  }

  // Cross-metal qualifier set but every tier row IS for the qualifying
  // metal itself — standard per-row own-metal qualification would behave
  // identically. Likely a mis-configuration.
  const qualifier = draft.tierQualifyingMetal
  if (qualifier) {
    const rows = safeArray(draft.metal_rates)
    const target = String(qualifier).toLowerCase()
    const everyTierIsQualifier = rows.length > 0
      && rows.every((row) => row && String(row.metalType || '').toLowerCase() === target)
    if (everyTierIsQualifier) {
      warnings.push('cross-metal qualifier set but every tier is for the qualifying metal itself; standard per-row qualification would behave identically')
    }
  }

  return warnings
}
